import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import SubscriptionModal from '../components/SubscriptionModal';
import { getCategories } from '../data/categoriesProvider';

const TOUR_STEPS: { id: string; title: string; body: string }[] = [
  { id: 'welcome', title: 'Welcome', body: 'This is your dashboard. Here you can see your progress, start studying, and track your goals.' },
  { id: 'header-dashboard', title: 'Dashboard icon', body: 'Click here anytime to return to this dashboard from other pages.' },
  { id: 'header-theme', title: 'Light & dark mode', body: 'Toggle between light and dark theme for comfortable studying at any time.' },
  { id: 'header-account', title: 'Account menu', body: 'Open your account menu to go to Profile & Settings or sign out.' },
  { id: 'progress', title: 'Progress overview', body: 'Your overall progress, questions answered, accuracy, streak, and today\'s activity.' },
  { id: 'exam-date', title: 'Exam date', body: 'Set your exam date to see days remaining and personalize your plan.' },
  { id: 'start-studying', title: 'Start studying', body: 'Study all cards, bookmarked or weak cards, quick practice, or take a practice test.' },
  { id: 'study-categories', title: 'Study by category', body: 'Focus on specific topics like Contracts, Agency, or Finance.' },
  { id: 'learning-goals', title: 'Learning goals', body: 'Daily question target and weekly accuracy goal.' },
];

const Dashboard: React.FC = () => {
  const { user, userProfile, userStats, signOut, darkMode, setDarkMode, updateUserProfile } = useAuth();
  
  // Prefer userProfile.state (DB) when present so DB updates (e.g. admin/MCP) are reflected; else localStorage; else ga
  const currentState = (userProfile?.state?.toLowerCase() || localStorage.getItem('selectedState')?.toLowerCase() || 'ga');
  const stateLabels: Record<string, string> = { ga: 'Georgia', fl: 'Florida', ca: 'California', tx: 'Texas', ny: 'New York', nj: 'New Jersey' };
  const stateLabel = stateLabels[currentState] || currentState.toUpperCase();

  // Get dynamic categories based on selected state
  const categories = getCategories(currentState);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimeoutOption, setShowTimeoutOption] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourRect, setTourRect] = useState<DOMRect | null>(null);
  const tourOverlayRef = useRef<HTMLDivElement>(null);

  const [examDate, setExamDate] = useState(userProfile?.exam_date || '');

  const navigate = useNavigate();
  const isFreeAccount = userProfile?.subscription_type === 'free';

  // Trial ending soon: show banner when user is on trial and ≤3 days left
  const trialEndTimestamp = userProfile?.subscription_expires_at
    ? new Date(userProfile.subscription_expires_at).getTime()
    : (() => {
        const stored = localStorage.getItem('trialEndDate');
        if (!stored) return null;
        const sec = parseInt(stored, 10);
        return Number.isFinite(sec) ? sec * 1000 : null;
      })();
  const daysUntilTrialEnd = trialEndTimestamp != null
    ? Math.ceil((trialEndTimestamp - Date.now()) / (24 * 60 * 60 * 1000))
    : null;
  const showTrialEndingBanner =
    userProfile?.subscription_type === 'trial' &&
    daysUntilTrialEnd != null &&
    daysUntilTrialEnd >= 0 &&
    daysUntilTrialEnd <= 3;

  // Free users can study (limited to 5 cards per category); paid/trial navigate normally
  const handleStudyAction = (path: string) => {
    navigate(path);
  };

  // Sync state: (1) If profile has state from DB, sync to localStorage. (2) If localStorage has state but profile doesn't, push to profile.
  useEffect(() => {
    if (!userProfile) return;
    if (userProfile.state) {
      localStorage.setItem('selectedState', userProfile.state);
    } else {
      const savedState = localStorage.getItem('selectedState');
      if (savedState) {
        updateUserProfile({ state: savedState }).catch(error => {
          console.error('Failed to update profile with saved state:', error);
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- userProfile omitted to avoid sync loops
  }, [userProfile?.state, updateUserProfile]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };







  const openDatePicker = () => {
    setShowDatePicker(true);
  };

  const closeDatePicker = () => {
    setShowDatePicker(false);
  };

  const saveExamDate = async () => {
    const input = document.getElementById('exam-date-input') as HTMLInputElement;
    if (input && input.value) {
      try {
        setExamDate(input.value);
        // Save to database via AuthContext
        await updateUserProfile({ exam_date: input.value });
        closeDatePicker();
        alert('Exam date saved successfully!');
      } catch (error) {
        console.error('Error saving exam date:', error);
        alert('Failed to save exam date. Please try again.');
      }
    }
  };




  const getDaysRemaining = () => {
    if (!examDate) return '--';
    const today = new Date();
    const exam = new Date(examDate);
    const days = Math.ceil((exam.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days.toString() : '0';
  };

  const scrollToTourTarget = useCallback((id: string) => {
    const el = document.querySelector(`[data-tour="${id}"]`);
    if (el && el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }, []);

  const clearTourHighlight = useCallback(() => {
    setTourRect(null);
  }, []);

  useEffect(() => {
    if (!tourActive || tourStep >= TOUR_STEPS.length) return;
    const step = TOUR_STEPS[tourStep];
    scrollToTourTarget(step.id);
    const t = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-tour="${step.id}"]`);
      if (el && el instanceof HTMLElement) setTourRect(el.getBoundingClientRect());
      else setTourRect(null);
    });
    return () => cancelAnimationFrame(t);
  }, [tourActive, tourStep, scrollToTourTarget]);

  const handleTourNext = () => {
    if (tourStep >= TOUR_STEPS.length - 1) {
      setTourActive(false);
      clearTourHighlight();
    } else setTourStep(s => s + 1);
  };
  const handleTourBack = () => {
    if (tourStep <= 0) return;
    setTourStep(s => s - 1);
  };
  const handleTourSkip = () => {
    setTourActive(false);
    clearTourHighlight();
  };

  // Check for active study session
  useEffect(() => {
    const checkActiveSession = async () => {
      if (user) {
        try {
                  // Session checking removed for now
        } catch (error) {
          console.error('Error checking active session:', error);
        }
      }
    };

    checkActiveSession();
  }, [user]);

  // Add timeout for loading screen - show continue option after 10 seconds
  useEffect(() => {
    if (user && (!userProfile || !userStats)) {
      const timeout = setTimeout(() => {
        setShowTimeoutOption(true);
      }, 10000); // 10 seconds

      return () => clearTimeout(timeout);
    }
  }, [user, userProfile, userStats]);

  // Update exam date when userProfile changes
  useEffect(() => {
    if (userProfile?.exam_date) {
      setExamDate(userProfile.exam_date);
    }
  }, [userProfile?.exam_date]);

  // Ensure page loads at top
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);



  // Only show loading if user is authenticated but profile/stats are still loading
  if (user && (!userProfile || !userStats)) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}>
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl p-12 max-w-md w-full mx-4 text-center`}>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
          <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Loading Your Dashboard</h2>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Preparing your personalized learning experience...</p>
          
          {showTimeoutOption && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
              <p className={`text-sm ${darkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
                Taking longer than expected? Try refreshing — if the server is slow, we&apos;ll load a minimal dashboard so you can continue.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="bg-yellow-500 hover:bg-yellow-600 text-yellow-900 font-semibold px-4 py-2 rounded-lg"
              >
                Refresh page
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If no user, let ProtectedRoute handle the redirect
  if (!user) {
    return null;
  }

  // Handle case where user continues anyway without profile/stats
  if (!userProfile || !userStats) {
    return (
      <div className={`min-h-screen transition-all duration-300 ${
        darkMode ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-gray-900'
      }`}>
        <Header 
          userMode="full"
          onSignOut={handleSignOut} 
          darkMode={darkMode} 
          onToggleDarkMode={() => setDarkMode(!darkMode)}
          userProfile={userProfile}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 text-center`}>
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Welcome to RealEstart!
            </h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Your profile is still loading. You can continue with limited functionality or refresh the page.
            </p>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={() => handleStudyAction(`/study?type=all&state=${currentState}`)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Start Studying
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TypeScript assertion - we know these exist after the checks above
  const stats = userStats!;
  const profile = userProfile!;

  return (
    <div className={`min-h-screen transition-all duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-slate-50 text-gray-900'}`}>
      {!darkMode && <div className="fixed inset-0 pointer-events-none bg-gradient-to-b from-blue-50/30 via-transparent to-transparent z-0" aria-hidden />}
      <div className="relative z-10">
        <Header 
          darkMode={darkMode} 
          onToggleDarkMode={() => setDarkMode(!darkMode)} 
          userMode="full" 
          onSignOut={handleSignOut}
          userProfile={profile}
        />

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="mb-10" data-tour="welcome">
            <p className={`text-sm font-medium tracking-wide uppercase ${darkMode ? 'text-blue-400/90' : 'text-blue-600/80'}`}>Dashboard</p>
            <h1 className={`text-3xl font-semibold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Welcome back, {profile.name}</h1>
            <p className={`text-sm mt-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Studying for: <span className="font-semibold">{stateLabel}</span></p>
          </div>

          {/* Trial ending soon */}
          {showTrialEndingBanner && (
            <div className="mb-8 p-5 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-amber-900 mb-0.5">Your free trial ends {daysUntilTrialEnd === 0 ? 'today' : daysUntilTrialEnd === 1 ? 'tomorrow' : `in ${daysUntilTrialEnd} days`}</h3>
                  <p className="text-amber-800 text-sm">After that you&apos;ll be charged $99/year. Cancel anytime in <button type="button" onClick={() => navigate('/profile')} className="underline font-medium">Profile</button> to avoid charges.</p>
                </div>
                <button onClick={() => navigate('/profile')} className="bg-amber-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-amber-900 transition-colors shrink-0">Manage subscription</button>
              </div>
            </div>
          )}

          {/* Upgrade banner for free accounts */}
          {isFreeAccount && (
            <div className="mb-8 p-5 rounded-xl bg-slate-800 text-white border border-slate-700">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold mb-0.5">Unlock full access</h3>
                  <p className="text-slate-300 text-sm">Get 500+ flashcards, practice tests, and progress tracking. 3-day free trial, then $99/year.</p>
                </div>
                <button onClick={() => setShowSubscriptionModal(true)} className="bg-white text-slate-800 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors shrink-0">Upgrade now</button>
              </div>
            </div>
          )}

          {/* Stats Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-10">
            <div className="lg:col-span-3" data-tour="progress">
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm border overflow-hidden ${darkMode ? 'border-gray-700 border-t-blue-500/20' : 'border-slate-200 border-t-blue-200'} border-t-2`}>
                <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className={`pl-4 border-l-4 border-blue-500 ${darkMode ? 'border-blue-400' : ''}`}>
                    <h2 className={`text-2xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Progress overview</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>Overall progress</span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{stats.progress.toFixed(1)}%</span>
                    </div>
                    <div className={`w-full rounded-full h-2.5 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}>
                      <div className={`h-2.5 rounded-full transition-all duration-500 ease-out ${darkMode ? 'bg-blue-500' : 'bg-blue-600'}`} style={{ width: `${Math.min(stats.progress, 100)}%` }} />
                    </div>
                    <div className={`flex justify-between text-xs mt-1.5 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                      <span>{stats.questions_answered} answered</span>
                      <span>{stats.total_questions} total</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { value: stats.questions_answered, label: 'Questions' },
                      { value: `${stats.accuracy.toFixed(1)}%`, label: 'Accuracy' },
                      { value: stats.current_streak, label: 'Day streak' },
                      { value: stats.daily_progress, label: 'Today' },
                    ].map((item, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700/50' : 'border-slate-200 bg-slate-50'}`}>
                        <div className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{item.value}</div>
                        <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Exam date */}
            <div className={`rounded-xl shadow-sm p-6 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`} data-tour="exam-date">
              <div className="flex items-center gap-2 mb-4">
                <svg className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>Exam date</h3>
              </div>
              <div className={`text-2xl font-semibold mb-1 cursor-pointer hover:opacity-80 ${darkMode ? 'text-white' : 'text-slate-900'}`} onClick={openDatePicker}>
                {examDate ? new Date(examDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Not set'}
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'} mb-4`}>{examDate ? new Date(examDate).getFullYear().toString() : ''}</div>
              <div className={`mt-auto pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
                <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-slate-500'} mb-0.5`}>Days remaining</div>
                <div className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{getDaysRemaining()}</div>
              </div>
              <button onClick={openDatePicker} className={`mt-4 w-full py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>Set exam date</button>
            </div>
          </div>

          {/* Start studying */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm border overflow-hidden mb-10 ${darkMode ? 'border-gray-700' : 'border-slate-200'}`} data-tour="start-studying">
            <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-slate-50 border-slate-200'}`}>
              <div className={`pl-4 border-l-4 ${darkMode ? 'border-blue-400' : 'border-blue-500'}`}>
                <h2 className={`text-2xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Start studying</h2>
              </div>
            </div>
            <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <button onClick={() => handleStudyAction(`/study?type=all&state=${currentState}`)} className={`group py-4 px-3 rounded-xl font-medium text-center border transition-colors ${darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-blue-400' : 'bg-slate-50 border-slate-200 hover:border-blue-300'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg></div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>Study All Cards</div>
            </button>
            <button onClick={() => handleStudyAction(`/study?type=bookmarked&state=${currentState}`)} className={`group py-4 px-3 rounded-xl font-medium text-center border transition-colors ${darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-amber-400' : 'bg-slate-50 border-slate-200 hover:border-amber-300'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>Study Bookmarked</div>
            </button>
            <button onClick={() => handleStudyAction(`/study?type=weak&state=${currentState}`)} className={`group py-4 px-3 rounded-xl font-medium text-center border transition-colors ${darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-red-400' : 'bg-slate-50 border-slate-200 hover:border-red-300'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg></div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>Study Weak Cards</div>
            </button>
            <button onClick={() => handleStudyAction(`/study?type=random&randomCount=50&state=${currentState}`)} className={`group py-4 px-3 rounded-xl font-medium text-center border transition-colors ${darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-green-400' : 'bg-slate-50 border-slate-200 hover:border-green-300'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg></div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>Quick Practice</div>
            </button>
            <button onClick={() => handleStudyAction(`/practice-test?state=${currentState}`)} className={`group py-4 px-3 rounded-xl font-medium text-center border transition-colors ${darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-purple-400' : 'bg-slate-50 border-slate-200 hover:border-purple-300'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${darkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>Practice Test</div>
            </button>
            <button onClick={() => navigate('/licensing-guide')} className={`group py-4 px-3 rounded-xl font-medium text-center border transition-colors ${darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-emerald-400' : 'bg-slate-50 border-slate-200 hover:border-emerald-300'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>
              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>Licensing Guide</div>
            </button>
          </div>
          <div className={`rounded-xl p-4 border ${darkMode ? 'bg-gray-700/30 border-gray-600' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>Quick practice</h3>
            <div className="grid grid-cols-3 gap-3">
              {[20, 50, 100].map((n) => (
                <button key={n} onClick={() => handleStudyAction(`/study?type=random&randomCount=${n}&state=${currentState}`)} className={`py-3 rounded-lg font-medium text-center transition-colors ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-white border border-slate-200 text-slate-700 hover:border-blue-300'}`}>
                  <span className="text-lg font-semibold">{n}</span><span className="block text-xs opacity-80">questions</span>
                </button>
              ))}
            </div>
          </div>
            </div>
          </div>

          {/* Study by category */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm border overflow-hidden mb-10 ${darkMode ? 'border-gray-700' : 'border-slate-200'}`} data-tour="study-categories">
            <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-slate-50 border-slate-200'}`}>
              <div className={`pl-4 border-l-4 ${darkMode ? 'border-blue-400' : 'border-blue-500'}`}>
                <h2 className={`text-2xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Study by category</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {categories.slice(0, 14).map((category, index) => (
                  <button
                    key={index}
                    onClick={() => handleStudyAction(`/study?type=category&category=${encodeURIComponent(category.name)}&state=${currentState}`)}
                    className={`group py-4 px-3 rounded-xl font-medium text-center border transition-colors ${darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 ${category.mutedBgDark && darkMode ? category.mutedBgDark : category.mutedBg || 'bg-slate-200'} ${category.mutedTextDark && darkMode ? category.mutedTextDark : category.mutedText || 'text-slate-600'}`}>
                      <span className="text-xs font-bold">{category.name.charAt(0)}</span>
                    </div>
                    <div className={`text-sm font-medium leading-tight ${darkMode ? 'text-white' : 'text-slate-800'}`}>{category.name}</div>
                    <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>{category.count} cards</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Learning goals */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm border overflow-hidden mb-10 ${darkMode ? 'border-gray-700' : 'border-slate-200'}`} data-tour="learning-goals">
            <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-slate-50 border-slate-200'}`}>
              <div className={`pl-4 border-l-4 ${darkMode ? 'border-blue-400' : 'border-blue-500'}`}>
                <h2 className={`text-2xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Learning goals</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>Daily target</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{stats.daily_progress}/25</p>
                  </div>
                  <div className={`w-full rounded-full h-2 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}>
                    <div className={`h-2 rounded-full transition-all ${darkMode ? 'bg-blue-500' : 'bg-blue-600'}`} style={{ width: `${Math.min((stats.daily_progress / 25) * 100, 100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>Weekly accuracy</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{stats.weekly_accuracy}%</p>
                  </div>
                  <div className={`w-full rounded-full h-2 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}>
                    <div className={`h-2 rounded-full transition-all ${darkMode ? 'bg-green-500' : 'bg-green-600'}`} style={{ width: `${Math.min(stats.weekly_accuracy, 100)}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Date Picker Modal */}
        {showDatePicker && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl p-8 max-w-md w-full border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Set Your Exam Date</h3>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>This helps us personalize your study plan</p>
              </div>
              <input 
                type="date" 
                id="exam-date-input" 
                className={`w-full p-4 border-2 rounded-xl mb-6 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-200 text-gray-900'
                }`}
                defaultValue={examDate || ''}
              />
              <div className="flex space-x-4">
                <button 
                  onClick={saveExamDate} 
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                >
                  Save Date
                </button>
                <button 
                  onClick={closeDatePicker} 
                  className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 hover:text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        </div>

        {/* Tour overlay with rounded cutout — dims page, cutout stays bright */}
        {tourActive && (
          <div ref={tourOverlayRef} className="fixed inset-0 z-40 pointer-events-none" aria-hidden>
            <svg className="absolute inset-0 w-full h-full" style={{ filter: 'drop-shadow(0 0 18px rgba(59, 130, 246, 0.4))' }}>
              <defs>
                <mask id="tour-cutout">
                  <rect width="100%" height="100%" fill="white" />
                  {tourRect && (
                    <rect
                      x={tourRect.x - 10}
                      y={tourRect.y - 10}
                      width={tourRect.width + 20}
                      height={tourRect.height + 20}
                      rx="12"
                      ry="12"
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#tour-cutout)" />
            </svg>
          </div>
        )}

        {/* Tour card and controls */}
        {tourActive && tourStep < TOUR_STEPS.length && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full mx-4 pointer-events-auto">
            <div className={`rounded-xl shadow-xl border p-4 ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-slate-200'}`}>
              <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>{TOUR_STEPS[tourStep].title}</h3>
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>{TOUR_STEPS[tourStep].body}</p>
              <div className="flex items-center justify-between mt-4 gap-3">
                <button type="button" onClick={handleTourSkip} className={`text-sm font-medium ${darkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}>Skip</button>
                <div className="flex gap-2">
                  <button type="button" onClick={handleTourBack} disabled={tourStep === 0} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-200 disabled:opacity-50' : 'bg-slate-100 text-slate-700 disabled:opacity-50'}`}>Back</button>
                  <button type="button" onClick={handleTourNext} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Next</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tour trigger — hidden during tour */}
        {!tourActive && (
          <button
            type="button"
            onClick={() => { setTourActive(true); setTourStep(0); }}
            className={`fixed bottom-6 right-6 z-30 w-10 h-10 rounded-full border-2 flex items-center justify-center shadow-lg transition-colors ${darkMode ? 'bg-gray-800 border-gray-600 hover:border-blue-400 text-gray-300' : 'bg-white border-slate-300 hover:border-blue-500 text-slate-600'}`}
            title="Dashboard tour"
            aria-label="Start dashboard tour"
          >
            <span className="text-lg font-bold leading-none">i</span>
          </button>
        )}
      </div>

      {showSubscriptionModal && (
        <SubscriptionModal
          darkMode={darkMode}
          onClose={() => setShowSubscriptionModal(false)}
          onUpgrade={() => setShowSubscriptionModal(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
